import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useIntl } from '@edx/frontend-platform/i18n';
import {
  Button, Dropdown, Pagination, SearchField, Spinner,
} from '@openedx/paragon';
import {
  RiArrowLeftSLine,
  RiCheckLine,
  RiFilterOffLine,
} from '@remixicon/react';
import { Link } from 'react-router-dom';

import { OngoingCoursesTable } from '../components';
import messages from '../components/messages';
import useOngoingCoursesQuery from '../useOngoingCoursesQuery';

import './index.scss';

const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 300;

const FILTER_TO_STATUS = {
  all: 'all',
  notStarted: 'not_started',
  inProgress: 'in_progress',
  completed: 'completed',
};

const FILTER_LABEL_KEYS = {
  all: 'filterAll',
  notStarted: 'filterNotStarted',
  inProgress: 'filterInProgress',
  completed: 'filterCompleted',
};

const AllOngoingCourses = () => {
  const intl = useIntl();
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [progressFilter, setProgressFilter] = useState('all');
  const [page, setPage] = useState(1);
  const debounceRef = useRef(null);

  const handleSearchChange = useCallback((value) => {
    setSearchInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearchTerm(value);
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
  }, []);

  const handleSearchClear = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setSearchInput('');
    setSearchTerm('');
    setPage(1);
  }, []);

  const handleSearchSubmit = useCallback((value) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setSearchInput(value);
    setSearchTerm(value);
    setPage(1);
  }, []);

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  const handleFilterChange = (key) => {
    setProgressFilter(key);
    setPage(1);
  };

  const {
    courses, pagination, isLoading, isFetching,
  } = useOngoingCoursesQuery({
    search: searchTerm,
    status: FILTER_TO_STATUS[progressFilter],
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  });

  const pageCount = Math.max(1, Math.ceil((pagination.total || 0) / PAGE_SIZE));

  const filterButtonLabel = progressFilter === 'all'
    ? intl.formatMessage(messages.filter)
    : intl.formatMessage(messages[FILTER_LABEL_KEYS[progressFilter]]);

  return (
    <div className="progress-page all-ongoing-courses">
      <div className="all-ongoing-courses__back">
        <Button
          variant="ghost-secondary"
          size="sm"
          as={Link}
          to="/progress"
          iconBefore={RiArrowLeftSLine}
        >
          {intl.formatMessage(messages.goBack)}
        </Button>
      </div>

      <h1 className="all-ongoing-courses__title">
        {intl.formatMessage(messages.ongoingCoursesTitle)}
      </h1>

      <div className="all-ongoing-courses__toolbar">
        <SearchField
          value={searchInput}
          onChange={handleSearchChange}
          onSubmit={handleSearchSubmit}
          onClear={handleSearchClear}
          placeholder={intl.formatMessage(messages.searchCoursesPlaceholder)}
          className="all-ongoing-courses__search"
          size="lg"
        />
        <Dropdown autoClose>
          <Dropdown.Toggle
            id="ongoing-courses-filter"
            as={Button}
            variant="outline-secondary"
            iconBefore={RiFilterOffLine}
            size="lg"
          >
            {filterButtonLabel}
          </Dropdown.Toggle>
          <Dropdown.Menu align="right">
            <Dropdown.Header>
              {intl.formatMessage(messages.filterByProgress)}
            </Dropdown.Header>
            {Object.keys(FILTER_LABEL_KEYS).map((key) => (
              <Dropdown.Item
                key={key}
                active={progressFilter === key}
                onClick={() => handleFilterChange(key)}
                className="all-ongoing-courses__filter-item"
              >
                <span>{intl.formatMessage(messages[FILTER_LABEL_KEYS[key]])}</span>
                {progressFilter === key && (
                  <RiCheckLine size={16} className="all-ongoing-courses__filter-check" />
                )}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </div>

      {isLoading ? (
        <div className="all-ongoing-courses__loading">
          <Spinner animation="border" screenReaderText={intl.formatMessage(messages.loadingCourses)} />
        </div>
      ) : (
        <>
          <div className={isFetching ? 'all-ongoing-courses__table--fetching' : undefined}>
            <OngoingCoursesTable
              courses={courses}
              limit={Infinity}
              hideSectionHeader
            />
          </div>

          {pageCount > 1 && (
            <div className="all-ongoing-courses__pagination">
              <Pagination
                paginationLabel={intl.formatMessage(messages.paginationLabel)}
                pageCount={pageCount}
                currentPage={page}
                onPageSelect={setPage}
                variant="reduced"
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AllOngoingCourses;
